import React, { useEffect, useMemo, useState } from "react";

const API = process.env.REACT_APP_API_BASE_URL || "";

// ★ グループ名（認証鍵）の手動上書き用（affiliateId → groupName）
type GroupOverrides = Record<string, string>;
const GROUP_OVERRIDES_KEY = "okm_aff_group_overrides";

/* ========= 型 ========= */


// /api/admin/orders/history から取得する生レコード
type OrderRow = {
  id?: string | number;
  orderId?: string | number;
  order_id?: string | number;
  userId?: string;
  user_id?: string;
  userID?: string;
  userName?: string;
  user_name?: string;
  planId?: string | number;
  plan_id?: string | number;
  planName?: string;
  plan_name?: string;
  amount?: number;
  amountJPY?: number;
  priceJPY?: number;
  pointSpent?: number;
  purchasedAt?: string;
  purchased_at?: string;
  date?: string;
  affiliateId?: string;
  affiliate_id?: string;
  affiliateID?: string;
  affiliateName?: string;
  affiliate_name?: string;
  affiliateGroup?: string;
  groupName?: string;
  group?: string;
  group_name?: string;
  groupId?: string;
  group_id?: string;
  groupKey?: string;
  group_key?: string;
  [key: string]: any;
};

type AffiliateSummaryRow = {
  affiliateId: string;
  name: string;
  groupName?: string;
  userCount: number; // ユニークユーザー数
  totalJPY: number; // 売上合計（円）
};

type GroupSummaryRow = {
  groupName: string;
  affiliateCount: number; // 媒体者数
  userCount: number; // ユニークユーザー数
  totalJPY: number;
};

type AffiliateSalesRow = {
  id?: string | number;
  userId?: string;
  userName?: string;
  planId?: string | number;
  planName?: string;
  amountJPY: number;
  purchasedAt?: string;
};

type AffiliateSalesResp = {
  affiliateId: string;
  affiliateName?: string;
  groupName?: string;
  userCount: number;
  orderCount: number;
  totalJPY: number;
  rows: AffiliateSalesRow[];
};

type GroupDetailRow = {
  affiliateId: string;
  name: string;
  userCount: number;
  totalJPY: number;
};

type GroupDetailResp = {
  groupName: string;
  totalJPY: number;
  userCount: number;
  affiliateCount: number;
  rows: GroupDetailRow[];
};

type TabMode = "affiliate" | "group";

type AffiliateMasterRow = {
  affiliateId: string;
  accountName: string;
  groupName: string;
};

/* ========= 共通ヘルパー ========= */


function safeNumber(n: any, fallback = 0): number {
  const v = typeof n === "string" ? Number(n) : n;
  return Number.isFinite(v) ? (v as number) : fallback;
}

function getAffiliateId(row: OrderRow): string {
  const raw =
    row.affiliateId ??
    row.affiliate_id ??
    row.affiliateID ??
    // ← ネストされた affiliate から拾う
    row.affiliate?.id ??
    row.affiliate?.affiliateId ??
    "";

  return raw ? String(raw).trim() : "";
}

function getAffiliateName(row: OrderRow, fallbackId: string): string {
  const raw =
    row.affiliateName ??
    row.affiliate_name ??
    row.affiliate?.name ??
    "";

  const name = raw ? String(raw).trim() : "";
  // ★ ここでは ID(コード) にはフォールバックしない
  //   「表示用の名前」は後段の集計で決める
  return name;
}


function getGroupName(row: OrderRow): string {
  const raw =
    row.groupName ??
    row.affiliateGroup ??
    row.group ??
    row.group_name ??
    row.groupId ??
    row.group_id ??
    row.groupKey ??
    row.group_key ??
    // ← ネストされた affiliate から拾う（正式）
    row.affiliate?.groupName ??
    row.affiliate?.group_key ??
    row.affiliate?.groupKey ??
    "";

  const name = raw ? String(raw).trim() : "";
  return name || "-";
}

// ★ 上書き情報（groupOverrides）を考慮した最終的なグループ名
function getFinalGroupName(row: OrderRow, overrides: GroupOverrides): string {
  const affiliateId = getAffiliateId(row);
  const base = getGroupName(row);
  const override = affiliateId ? overrides[affiliateId] : "";
  const name = (override || base || "-").trim();
  return name || "-";
}



function getUserId(row: OrderRow): string {
  const raw =
    row.userId ??
    row.user_id ??
    row.userID ??
    row.user?.id ??
    "";
  return raw ? String(raw).trim() : "";
}

function getUserName(row: OrderRow): string {
  const raw =
    row.userName ??
    row.user_name ??
    row.user?.name ??
    "";
  return raw ? String(raw).trim() : "";
}

function getPlanId(row: OrderRow): string | number | undefined {
  return row.planId ?? row.plan_id;
}

function getPlanName(row: OrderRow, fallbackId?: string | number): string {
  const raw = row.planName ?? row.plan_name ?? "";
  const name = raw ? String(raw).trim() : "";
  if (name) return name;
  if (fallbackId == null) return "";
  return `#${fallbackId}`;
}

function getAmountJPY(row: OrderRow): number {
  return safeNumber(row.amountJPY ?? row.amount ?? row.priceJPY, 0);
}

function getPurchasedAt(row: OrderRow): string | undefined {
  return row.purchasedAt ?? row.purchased_at ?? row.date ?? undefined;
}

// ★ この注文が「課金（ポイント購入）」かどうか判定するヘルパー
function isChargeOrder(row: OrderRow): boolean {
  const amount = getAmountJPY(row);
  if (amount <= 0) return false;

  // type / kind / category などに point_purchase が入っているケースを優先
  const t = typeof (row as any).type === "string" ? (row as any).type : "";
  const kind =
    typeof (row as any).kind === "string"
      ? (row as any).kind
      : typeof (row as any).category === "string"
      ? (row as any).category
      : "";

  if (t === "point_purchase" || kind === "point_purchase") {
    return true;
  }

  // planId が入っているものは「プラン購入」とみなして除外
  const pid = getPlanId(row);
  if (pid != null && pid !== "" && Number(pid) !== 0) {
    return false;
  }

  // 上記に当てはまらず、金額だけ入っているものは課金として扱う
  return true;
}

/* === 日付ヘルパー（JST基準でYYYY-MM-DD） === */

function toJST(d: Date) {

  const tz = d.getTimezoneOffset(); // 分
  return new Date(d.getTime() + (tz + -540) * 60 * 1000); // JST = -540分
}

function fmtYMD(d: Date) {
  const j = toJST(d);
  const y = j.getFullYear();
  const m = String(j.getMonth() + 1).padStart(2, "0");
  const da = String(j.getDate()).padStart(2, "0");
  return `${y}-${m}-${da}`;
}

function startOfMonthJST(d = new Date()) {
  const j = toJST(d);
  return new Date(j.getFullYear(), j.getMonth(), 1);
}

function todayJST() {
  return toJST(new Date());
}

/* ========= 本体 ========= */

export default function AffiliateSales() {
  // ★ グループ名（認証鍵）の手動上書き（affiliateId → groupName）
  const [groupOverrides, setGroupOverrides] = useState<GroupOverrides>({});

  // ★ 媒体者マスタ（affiliateId → accountName）のキャッシュ
  const [affiliateNameMap, setAffiliateNameMap] =
    useState<Record<string, string>>({});

  // ★ サイト全体の集計用（ダッシュボードと同じ母数）
  const [siteTotalSalesJPY, setSiteTotalSalesJPY] = useState<number>(0);
  const [siteAffUserCount, setSiteAffUserCount] = useState<number>(0);

  // ★ 媒体者マスタ一覧（グループ名付き）
  const [affiliateMaster, setAffiliateMaster] = useState<AffiliateMasterRow[]>(
    []
  );

  // ★ 媒体者ごとの紐づきユーザー集合
  const [affUserMap, setAffUserMap] = useState<Record<string, Set<string>>>({});

  // 起動時に localStorage から復元
  useEffect(() => {

    try {
      const raw = window.localStorage.getItem(GROUP_OVERRIDES_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === "object") {
          setGroupOverrides(parsed);
        }
      }
    } catch {
      // noop
    }
  }, []);

  // ★ 媒体者マスタを取得して
  //    - affiliateId → 名前
  //    - グループ名（認証鍵）
  //   をマスタとして保持
  useEffect(() => {
    (async () => {
      try {
        const qs = new URLSearchParams();
        qs.set("status", "all");
        qs.set("query", "");

        const res = await fetch(
          `${API}/api/admin/affiliates?${qs.toString()}`,
          {
            headers: { "x-api-key": "admin-dev" },
          }
        );
        if (!res.ok) {
          console.error("failed to fetch affiliates");
          return;
        }

        const json: any = await res.json();
        const list: any[] = Array.isArray(json) ? json : [];

        const mapName: Record<string, string> = {};
        const master: AffiliateMasterRow[] = [];

        list.forEach((a) => {
          const affId = String(
            a.affiliateId ?? a.code ?? ""
          ).trim();
          if (!affId) return;

          const name = String(
            a.accountName ?? a.name ?? ""
          ).trim();

          // ★ groupName が空文字のときは key などからフォールバック
          const rawGroupName =
            a.groupName ||
            a.group_name ||
            a.group ||
            a.groupKey ||
            a.group_key ||
            a.key ||
            "";
          const groupName = String(rawGroupName).trim();

          mapName[affId] = name || affId;
          master.push({
            affiliateId: affId,
            accountName: name || affId,
            groupName: groupName || "-",
          });
        });

        setAffiliateNameMap(mapName);
        setAffiliateMaster(master);

      } catch (e) {
        console.error("error on fetching affiliates", e);
      }
    })();
  }, []);


  // ★ サイト全体「紐づきユーザー数」と
  //    媒体者ごとのユーザー集合を /admin/users から集計
  useEffect(() => {
    (async () => {
      try {
        const qs = new URLSearchParams();
        qs.set("page", "1");
        qs.set("perPage", "1000"); // 当面 1000 ユーザーまで
        qs.set("status", "all");

        const res = await fetch(
          `${API}/api/admin/users?${qs.toString()}`,
          {
            headers: { "x-api-key": "admin-dev" },
          }
        );
        if (!res.ok) {
          console.error("failed to fetch users for aff user count");
          setSiteAffUserCount(0);
          setAffUserMap({});
          return;
        }

        const json: any = await res.json();
        const list: any[] = Array.isArray(json?.items)
          ? json.items
          : Array.isArray(json)
          ? json
          : [];

        const userSet = new Set<string>();
        const map: Record<string, Set<string>> = {};

        list.forEach((u: any) => {
          const uid = String(
            u.id ?? u.userId ?? u.user_id ?? ""
          ).trim();
          const affId = String(
            u.affiliateId ??
              u.affiliate_id ??
              u.affiliate?.affiliateId ??
              ""
          ).trim();

          if (uid && affId) {
            userSet.add(uid);
            if (!map[affId]) {
              map[affId] = new Set<string>();
            }
            map[affId].add(uid);
          }
        });

        setSiteAffUserCount(userSet.size);
        setAffUserMap(map);
      } catch (e) {
        console.error("error on fetching users for aff user count", e);
        setSiteAffUserCount(0);
        setAffUserMap({});
      }
    })();
  }, []);



  // URL クエリから初期値取得
  const initQuery = useMemo(() => {

    try {
      const u = new URL(window.location.href);
      const rawTab = (u.searchParams.get("tab") as TabMode | null) || "affiliate";
      const tabQ: TabMode = rawTab === "group" ? "group" : "affiliate";

      const fromQ = u.searchParams.get("from") || "";
      const toQ = u.searchParams.get("to") || "";
      const affiliateQ = u.searchParams.get("affiliateId") || "";
      const groupQ = u.searchParams.get("group") || "";
      return { tabQ, fromQ, toQ, affiliateQ, groupQ };
    } catch {
      return {
        tabQ: "affiliate" as TabMode,
        fromQ: "",
        toQ: "",
        affiliateQ: "",
        groupQ: "",
      };
    }
  }, []);

   const [tab, setTab] = useState<TabMode>(initQuery.tabQ);

  // 期間
  const [from, setFrom] = useState<string>(initQuery.fromQ);
  const [to, setTo] = useState<string>(initQuery.toQ);

  // 生の注文一覧（この 1本だけを一次情報とする）
  const [orders, setOrders] = useState<OrderRow[]>([]);
  const [loading, setLoading] = useState(false);


  // 選択中アフィ / グループ
  // ★ 初期状態では何も選択されていない
  const [selectedAffiliateId, setSelectedAffiliateId] = useState<string>("");

  const [showAffiliateDetail, setShowAffiliateDetail] = useState<boolean>(false);

  // グループ詳細は「売上詳細」ボタンを押したときだけ選択する
  const [selectedGroup, setSelectedGroup] = useState<string>("");
  const [showGroupDetail, setShowGroupDetail] = useState<boolean>(false);



  /* ===== 初期表示 ===== */

  useEffect(() => {
    // 期間未指定なら「今月」をデフォルト
    if (!from || !to) {
      const start = startOfMonthJST();
      const end = todayJST();
      const fromStr = fmtYMD(start);
      const toStr = fmtYMD(end);
      setFrom(fromStr);
      setTo(toStr);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

const fetchOrders = async () => {
  setLoading(true);
  try {
    const qs = new URLSearchParams();
    if (from) qs.set("from", from);
    if (to) qs.set("to", to);

    const r = await fetch(
      `${API}/api/admin/orders/history?${qs.toString()}`,
      {
        headers: { "x-api-key": "admin-dev" },
      }
    );
    if (!r.ok) throw new Error("orders_fetch_failed");

    // ★ JSON 取得（{ items: [...] } or [ ... ] 両対応）
    const json: any = await r.json();
    const rawRows: any[] = Array.isArray(json)
      ? json
      : Array.isArray(json?.items)
      ? json.items
      : [];

    // ======== 正規化レイヤー ========
    const normalized = rawRows.map((r: any) => {
      return {
        // 元のフィールドは全部残す（affiliate などのネストも保持）
        ...r,

        id: String(r.id ?? r.orderId ?? r.order_id ?? ""),

        // ユーザーID（ネスト user.id も見る）
        userId: String(
          r.userId ??
            r.user_id ??
            r.userID ??
            r.user?.id ??
            ""
        ),

        // ユーザー名（できるだけ全部拾う）
        userName: String(
          r.userName ??
            r.user_name ??
            r.buyer ??
            r.buyer_name ??
            r.name ??
            r.user?.name ??
            r.user?.username ??
            ""
        ),

        planId: r.planId ?? r.plan_id ?? null,
        planName:
          r.planName ??
          r.plan_name ??
          (r.type === "point_purchase" ? "ポイント購入" : ""),

        amountJPY: Number(
          r.priceJPY ??
            r.amountJPY ??
            r.amount ??
            0
        ),

        // ★ 媒体者ID（affiliateのネストも拾う）
        affiliateId: String(
          r.affiliateId ??
            r.affiliate_id ??
            r.affiliateID ??
            r.affiliate?.id ??
            r.affiliate?.affiliateId ??
            ""
        ),

        // ★ 媒体者名（けんじ など）を保持
        affiliateName: String(
          r.affiliateName ??
            r.affiliate_name ??
            r.affiliate?.name ??
            ""
        ),

        // グループ名（認証鍵）…できるだけ全部拾う
        groupName: String(
          r.groupName ??
            r.group_name ??
            r.group ??
            r.groupId ??
            r.group_id ??
            r.groupID ??
            r.groupKey ??
            r.group_key ??
            r.key ??                  // key だけのパターン
            r.affiliateGroup ??
            r.affiliate?.groupName ??
            r.affiliate?.group_key ??
            r.affiliate?.groupKey ??
            r.affiliate?.group ??     // affiliate.group パターン
            ""
        ),

        purchasedAt: String(
          r.date ??
            r.purchasedAt ??
            r.purchased_at ??
            ""
        ),
      };
    });

    // ★ 正規化後で置き換え
    setOrders(normalized);

    // ★ サイト全体の売上合計（ポイント購入＝課金だけを合計）
    const total = normalized.reduce((sum: number, row: any) => {
      if (!isChargeOrder(row)) return sum;
      const amount = getAmountJPY(row);
      return amount > 0 ? sum + amount : sum;
    }, 0);
    setSiteTotalSalesJPY(total);

  } catch (e) {
    console.error(e);
    setOrders([]);
    setSiteTotalSalesJPY(0);
  } finally {
    setLoading(false);
  }
};


// ★ グループ（認証鍵）名を手動登録 / 変更する
const handleSetGroupName = (affiliateId: string, current?: string) => {
  if (!affiliateId) return;

  const input = window.prompt(
    "このアフィリエイターのグループ名（認証鍵）を入力してください。",
    current || ""
  );
  if (input === null) return; // キャンセル

  const name = input.trim();

  setGroupOverrides((prev) => {
    const next: GroupOverrides = { ...prev };
    if (name) {
      next[affiliateId] = name;
    } else {
      // 空なら削除
      delete next[affiliateId];
    }
    try {
      window.localStorage.setItem(GROUP_OVERRIDES_KEY, JSON.stringify(next));
    } catch {
      // noop
    }
    return next;
  });
};

// 初回＆期間変更時に自動で集計
useEffect(() => {
  if (!from || !to) return;
  fetchOrders();
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [from, to]);


  /* ===== URL クエリ同期 ===== */

  useEffect(() => {
    try {
      const u = new URL(window.location.href);
      u.searchParams.set("tab", tab);

      if (from) u.searchParams.set("from", from);
      else u.searchParams.delete("from");

      if (to) u.searchParams.set("to", to);
      else u.searchParams.delete("to");

      if (selectedAffiliateId)
        u.searchParams.set("affiliateId", selectedAffiliateId);
      else u.searchParams.delete("affiliateId");

      if (selectedGroup) u.searchParams.set("group", selectedGroup);
      else u.searchParams.delete("group");

      window.history.replaceState(null, "", u.toString());
    } catch {
      // noop
    }
  }, [tab, from, to, selectedAffiliateId, selectedGroup]);

  /* ===== orders から各種集計を作る ===== */

  // 課金（ポイント購入）だけ抜き出す
  const chargeOrders = useMemo(() => {
    return (orders || []).filter((row) => isChargeOrder(row));
  }, [orders]);

  // affiliateId が入っている課金レコードだけ対象（アフィ集計用）
  const affiliateOrders = useMemo(() => {
    return chargeOrders.filter((row) => {
      const affiliateId = getAffiliateId(row);
      const amountJPY = getAmountJPY(row);
      return affiliateId && amountJPY > 0;
    });
  }, [chargeOrders]);

  // ★ サイト全体カード用：
  //   - 売上合計 → 全課金合計（siteTotalSalesJPY）
  //   - 紐づきユーザー数 → affiliateId を持つユーザー総数（siteAffUserCount）
  const overallTotal = siteTotalSalesJPY;
  const overallUserCount = siteAffUserCount;



  // アフィリエイター別サマリー
  const affiliateList: AffiliateSummaryRow[] = useMemo(() => {
    // ★ まず媒体者マスタ全件をベースに枠を作る
    const map = new Map<
      string,
      AffiliateSummaryRow & { users: Set<string> }
    >();

    affiliateMaster.forEach((a) => {
      const affiliateId = a.affiliateId;
      if (!affiliateId) return;

      const name = a.accountName || affiliateId;

      // 手動上書きがあれば最優先
      const overrideGroup = groupOverrides[affiliateId] || "";
      const baseGroup =
        overrideGroup ||
        (a.groupName && a.groupName !== "-" ? a.groupName : "");
      const groupName = baseGroup || "";

      map.set(affiliateId, {
        affiliateId,
        name,
        groupName,
        totalJPY: 0,
        userCount: 0,
        users: new Set<string>(),
      });
    });

    // ★ 次に「課金レコード」から売上とユーザー集合を足していく
    affiliateOrders.forEach((row) => {
      const affiliateId = getAffiliateId(row);
      if (!affiliateId) return;

      const amountJPY = getAmountJPY(row);
      const uid = getUserId(row);

      // 注文側の名前
      const nameFromOrder = getAffiliateName(row, affiliateId);
      // マスタ側
      const masterName = affiliateNameMap[affiliateId];
      const displayName = nameFromOrder || masterName || affiliateId;

      const finalGroupName = getFinalGroupName(row, groupOverrides);

      if (!map.has(affiliateId)) {
        // ★ マスタにいない「野良アフィ」も一応拾う
        map.set(affiliateId, {
          affiliateId,
          name: displayName,
          groupName: finalGroupName === "-" ? "" : finalGroupName,
          totalJPY: 0,
          userCount: 0,
          users: new Set<string>(),
        });
      }

      const rec = map.get(affiliateId)!;

      // 名前の補完（IDだけの場合に上書き）
      if (!rec.name || rec.name === rec.affiliateId) {
        if (nameFromOrder) {
          rec.name = nameFromOrder;
        } else if (masterName) {
          rec.name = masterName;
        }
      }

      // グループ名の補完
      if (
        (!rec.groupName || rec.groupName === "-") &&
        finalGroupName &&
        finalGroupName !== "-"
      ) {
        rec.groupName = finalGroupName;
      }

      rec.totalJPY += amountJPY;
      if (uid) {
        rec.users.add(uid);
      }
    });

    const list: AffiliateSummaryRow[] = [];
    map.forEach((v) => {
      // ★ 紐づきユーザー集合（/admin/users ベース）を優先
      const linkedUsers = affUserMap[v.affiliateId];
      const userCount =
        linkedUsers && linkedUsers.size
          ? linkedUsers.size
          : v.users.size;

      list.push({
        affiliateId: v.affiliateId,
        name: v.name,
        groupName: v.groupName,
        totalJPY: v.totalJPY,
        userCount,
      });
    });

    // グループ名→アフィ名でソート（グループ未設定は一番下）
    return list.sort((a, b) => {
      const ga = a.groupName || "~~~"; // 未設定は最後に
      const gb = b.groupName || "~~~";
      if (ga < gb) return -1;
      if (ga > gb) return 1;
      const na = a.name || "";
      const nb = b.name || "";
      if (na < nb) return -1;
      if (na > nb) return 1;
      return 0;
    });
  }, [affiliateOrders, groupOverrides, affiliateNameMap, affUserMap, affiliateMaster]);


  // グループ別サマリー
  const groupList: GroupSummaryRow[] = useMemo(() => {
    type GroupAgg = {
      groupName: string;
      totalJPY: number;
      affiliateIds: Set<string>;
      userIds: Set<string>;
    };

    const map = new Map<string, GroupAgg>();

    // 1) 媒体者マスタ＋ユーザー紐付きから
    affiliateMaster.forEach((a) => {
      // ★ 手動上書き（affiliateId → groupName）を最優先
      const overrideName = groupOverrides[a.affiliateId] || "";
      const rawGroup =
        overrideName ||
        (a.groupName && a.groupName !== "-" ? a.groupName : "");
      const key = rawGroup || "-";

      if (!map.has(key)) {
        map.set(key, {
          groupName: key,
          totalJPY: 0,
          affiliateIds: new Set<string>(),
          userIds: new Set<string>(),
        });
      }
      const rec = map.get(key)!;
      rec.affiliateIds.add(a.affiliateId);

      const users = affUserMap[a.affiliateId];
      if (users) {
        users.forEach((uid) => rec.userIds.add(uid));
      }
    });

    // 2) 売上合計は「課金レコード」から加算
    affiliateOrders.forEach((row) => {
      const affiliateId = getAffiliateId(row);
      if (!affiliateId) return;

      const groupNameRaw = getFinalGroupName(row, groupOverrides);
      const key =
        groupNameRaw && groupNameRaw !== "-" ? groupNameRaw : "-";

      if (!map.has(key)) {
        map.set(key, {
          groupName: key,
          totalJPY: 0,
          affiliateIds: new Set<string>([affiliateId]),
          userIds: new Set<string>(),
        });
      }
      const rec = map.get(key)!;
      rec.totalJPY += getAmountJPY(row);
    });

    const list: GroupSummaryRow[] = [];
    map.forEach((v) => {
      list.push({
        groupName: v.groupName,
        totalJPY: v.totalJPY,
        affiliateCount: v.affiliateIds.size,
        userCount: v.userIds.size,
      });
    });

    return list.sort((a, b) => {
      const ga = a.groupName || "";
      const gb = b.groupName || "";
      if (ga < gb) return -1;
      if (ga > gb) return 1;
      return 0;
    });
  }, [affiliateMaster, affUserMap, affiliateOrders, groupOverrides]);

  /* ===== アフィ詳細・グループ詳細を作る ===== */

  const affiliateSales: AffiliateSalesResp | null = useMemo(() => {
    if (!selectedAffiliateId) return null;

    const rows: AffiliateSalesRow[] = [];
    const userSet = new Set<string>();
    let total = 0;

    affiliateOrders.forEach((row) => {
      const affiliateId = getAffiliateId(row);
      if (affiliateId !== selectedAffiliateId) return;

      const amountJPY = getAmountJPY(row);
      if (amountJPY <= 0) return;

      const uid = getUserId(row);
      const uname = getUserName(row);
      const pid = getPlanId(row);
      const pname = getPlanName(row, pid);
      const dt = getPurchasedAt(row);

      rows.push({
        id: row.id ?? row.orderId ?? row.order_id,
        userId: uid || undefined,
        userName: uname || undefined,
        planId: pid,
        planName: pname || undefined,
        amountJPY,
        purchasedAt: dt,
      });

      if (uid) userSet.add(uid);
      total += amountJPY;
    });

    const affSummary = affiliateList.find(
      (a) => a.affiliateId === selectedAffiliateId
    );

    return {
      affiliateId: selectedAffiliateId,
      affiliateName: affSummary?.name || selectedAffiliateId,
      groupName: affSummary?.groupName,
      userCount: userSet.size,
      orderCount: rows.length,
      totalJPY: total,
      rows,
    };
  }, [affiliateOrders, selectedAffiliateId, affiliateList]);

  const groupDetail: GroupDetailResp | null = useMemo(() => {
    if (!selectedGroup) return null;

    const rows: GroupDetailRow[] = [];
    let total = 0;
    let userTotal = 0;

    affiliateList.forEach((a) => {
      const gname = a.groupName || "-";
      if (gname !== selectedGroup) return;

      rows.push({
        affiliateId: a.affiliateId,
        name: a.name || a.affiliateId,
        userCount: a.userCount,
        totalJPY: a.totalJPY,
      });

      total += a.totalJPY;
      userTotal += a.userCount;
    });

    return {
      groupName: selectedGroup,
      totalJPY: total,
      userCount: userTotal,
      affiliateCount: rows.length,
      rows,
    };
  }, [selectedGroup, affiliateList]);

  /* ===== ページング ===== */

  const [affiliatePage, setAffiliatePage] = useState(0);
  const AFF_ROWS_PER_PAGE = 50;
  const affiliatePageRows = useMemo(() => {
    const start = affiliatePage * AFF_ROWS_PER_PAGE;
    const end = start + AFF_ROWS_PER_PAGE;
    return affiliateList.slice(start, end);
  }, [affiliateList, affiliatePage]);

  const [groupPage, setGroupPage] = useState(0);
  const GROUP_ROWS_PER_PAGE = 50;
  const groupPageRows = useMemo(() => {
    const start = groupPage * GROUP_ROWS_PER_PAGE;
    const end = start + GROUP_ROWS_PER_PAGE;
    return groupList.slice(start, end);
  }, [groupList, groupPage]);

  /* ===== レンダリング ===== */

  return (
    <div style={{ padding: 16 }}>
      <h2 style={{ margin: 0, marginBottom: 12, fontWeight: 800 }}>
        アフィリエイト売上管理（期間集計）
      </h2>

      {/* タブ */}
      <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
        <button
          type="button"
          onClick={() => setTab("affiliate")}
          disabled={tab === "affiliate"}
          style={{
            ...btnTab,
            ...(tab === "affiliate" ? btnTabActive : {}),
          }}
        >
          アフィリエイター
        </button>
        <button
          type="button"
          onClick={() => setTab("group")}
          disabled={tab === "group"}
          style={{
            ...btnTab,
            ...(tab === "group" ? btnTabActive : {}),
          }}
        >
          グループ
        </button>
      </div>

      {/* 共通フィルタ */}
      <div
        style={{
          display: "flex",
          gap: 8,
          flexWrap: "wrap",
          alignItems: "center",
          marginBottom: 12,
        }}
      >
        <input
          type="date"
          value={from}
          onChange={(e) => setFrom(e.target.value)}
          style={input}
        />
        <input
          type="date"
          value={to}
          onChange={(e) => setTo(e.target.value)}
          style={input}
        />

        {/* 期間プリセット：Affiliate は今月だけ / Group は3ボタン */}
{tab === "affiliate" ? (
  <div style={{ display: "flex", gap: 6 }}>
    <button
      type="button"
      style={btnTab}
      onClick={() => {
        const start = startOfMonthJST();
        const end = todayJST();
        setFrom(fmtYMD(start));
        setTo(fmtYMD(end));
      }}
    >
      今月
    </button>
  </div>
) : (
  <div style={{ display: "flex", gap: 6 }}>
    <button
      type="button"
      style={btnTab}
      onClick={() => {
        const start = startOfMonthJST();
        const end = todayJST();
        setFrom(fmtYMD(start));
        setTo(fmtYMD(end));
      }}
    >
      今月
    </button>
  </div>
)}

        {/* 再集計ボタン */}
        <button
          type="button"
          onClick={fetchOrders}
          style={btnPrimary}
          disabled={loading}
        >
          {loading ? "集計中…" : "集計"}
        </button>
      </div>

      {tab === "affiliate" ? (
        <AffiliateTabView
          overallTotal={overallTotal}
          overallUserCount={overallUserCount}
          affiliatePageRows={affiliatePageRows}
          affiliateTotalCount={affiliateList.length}
          affiliatePage={affiliatePage}
          onChangeAffiliatePage={setAffiliatePage}
          onClickShowDetail={(row) => {
            // ★ 同じアフィをもう一度押したらトグル
            if (selectedAffiliateId === row.affiliateId) {
              setShowAffiliateDetail((prev) => !prev);
            } else {
              setSelectedAffiliateId(row.affiliateId);
              setShowAffiliateDetail(true);
            }
          }}
          selectedAffiliateId={selectedAffiliateId}
          showAffiliateDetail={showAffiliateDetail}
          affiliateSales={affiliateSales}
          affiliateSalesLoading={loading}
          onClickEditGroupName={handleSetGroupName}   // ★ 追加
        />
      ) : (



         <GroupTabView
          overallTotal={overallTotal}
          overallUserCount={overallUserCount}
          groupPageRows={groupPageRows}
          groupTotalCount={groupList.length}
          groupPage={groupPage}
          onChangeGroupPage={setGroupPage}
          onClickShowGroupDetail={(row) => {
            // ★ 同じグループをもう一度押したらトグル
            if (selectedGroup === row.groupName) {
              setShowGroupDetail((prev) => !prev);
            } else {
              setSelectedGroup(row.groupName);
              setShowGroupDetail(true);
            }
          }}
          selectedGroup={selectedGroup}
          showGroupDetail={showGroupDetail}
          groupDetail={groupDetail}
          groupDetailLoading={loading}
        />

      )}
    </div>
  );
}

/* ========= サブビュー：アフィタブ ========= */

type AffiliateTabProps = {
  overallTotal: number;
  overallUserCount: number;
  affiliatePageRows: AffiliateSummaryRow[];
  affiliateTotalCount: number;
  affiliatePage: number;
  onChangeAffiliatePage: (page: number) => void;
  onClickShowDetail: (row: AffiliateSummaryRow) => void;
  selectedAffiliateId: string;
  showAffiliateDetail: boolean;
  affiliateSales: AffiliateSalesResp | null;
  affiliateSalesLoading: boolean;
  onClickEditGroupName: (affiliateId: string, currentGroupName?: string) => void; // ★ 追加
};




function AffiliateTabView(props: AffiliateTabProps) {
  const {
    overallTotal,
    overallUserCount,
    affiliatePageRows,
    affiliateTotalCount,
    affiliatePage,
    onChangeAffiliatePage,
    onClickShowDetail,
    selectedAffiliateId,
    showAffiliateDetail,
    affiliateSales,
    affiliateSalesLoading,
    onClickEditGroupName, // ★ 追加
  } = props;


  const AFF_ROWS_PER_PAGE = 50;
  const totalPages = Math.max(
    1,
    Math.ceil(affiliateTotalCount / AFF_ROWS_PER_PAGE)
  );

  const handlePrev = () => {
    onChangeAffiliatePage(Math.max(0, affiliatePage - 1));
  };
  const handleNext = () => {
    if ((affiliatePage + 1) * AFF_ROWS_PER_PAGE < affiliateTotalCount) {
      onChangeAffiliatePage(affiliatePage + 1);
    }
  };

  const salesRows = affiliateSales?.rows ?? [];
  const salesTotal = safeNumber(affiliateSales?.totalJPY, 0);
  const salesUserCount = safeNumber(affiliateSales?.userCount, 0);
  const salesOrderCount = safeNumber(affiliateSales?.orderCount, 0);
  const salesGroupName = affiliateSales?.groupName ?? "";
  const salesAffiliateName = affiliateSales?.affiliateName ?? "";
  const salesAffiliateId = affiliateSales?.affiliateId ?? selectedAffiliateId;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {/* サイト全体サマリー */}
      <div style={{ display: "flex", gap: 16 }}>
        <SummaryCard
          label="サイト全体 売上合計"
          value={`¥${safeNumber(overallTotal, 0).toLocaleString()}`}
        />
        <SummaryCard
          label="サイト全体 紐づきユーザー数"
          value={`${safeNumber(overallUserCount, 0).toLocaleString()} 人`}
        />
      </div>

      {/* アフィ一覧（全体） */}
      <div
        style={{
          overflowX: "auto",
          background: "#fff",
          border: "1px solid #e5e7eb",
          borderRadius: 12,
        }}
      >
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ background: "#f8fafc" }}>
              <th style={th}>グループ（認証鍵）</th>
              <th style={th}>アフィリエイター</th>
              <th style={th}>ID</th>
              <th style={{ ...th, textAlign: "right" }}>ユーザー数</th>
              <th style={{ ...th, textAlign: "right" }}>売上額(円)</th>
              <th style={{ ...th, textAlign: "center" }}></th>
            </tr>
          </thead>
          <tbody>
            {affiliatePageRows.map((r) => (
              <tr
                key={r.affiliateId}
                style={{
                  borderTop: "1px solid #eef2f7",
                  background:
                    selectedAffiliateId === r.affiliateId
                      ? "#eff6ff"
                      : "#fff",
                }}
              >
                <td style={td}>
                  <span style={{ marginRight: 6 }}>{r.groupName || "-"}</span>
                  <button
                    type="button"
                    style={btnLink}
                    onClick={() =>
                      onClickEditGroupName(r.affiliateId, r.groupName)
                    }
                  >
                    {r.groupName ? "変更" : "登録"}
                  </button>
                </td>
                <td style={td}>{r.name || r.affiliateId}</td>

                <td style={td}>{r.affiliateId}</td>
                <td style={{ ...td, textAlign: "right" }}>
                  {(r.userCount ?? 0).toLocaleString()} 人
                </td>
                <td style={{ ...td, textAlign: "right" }}>
                  ¥{(r.totalJPY ?? 0).toLocaleString()}
                </td>
                <td style={{ ...td, textAlign: "center" }}>
                  <button
                    type="button"
                    style={btnLink}
                    onClick={() => onClickShowDetail(r)}
                  >
                    売上詳細
                  </button>
                </td>
              </tr>
            ))}
            {!affiliatePageRows.length && (
              <tr>
                <td
                  colSpan={6}
                  style={{
                    padding: 16,
                    textAlign: "center",
                    color: "#94a3b8",
                  }}
                >
                  データがありません
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* ページャー */}
      {affiliateTotalCount > AFF_ROWS_PER_PAGE && (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            fontSize: 12,
            color: "#64748b",
          }}
        >
          <div>
            {affiliateTotalCount
              ? `全 ${affiliateTotalCount.toLocaleString()} 件中 ${
                  affiliatePage * AFF_ROWS_PER_PAGE + 1
                }〜${Math.min(
                  (affiliatePage + 1) * AFF_ROWS_PER_PAGE,
                  affiliateTotalCount
                ).toLocaleString()} 件を表示`
              : "0 件"}
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button
              type="button"
              style={btnPager}
              onClick={handlePrev}
              disabled={affiliatePage === 0}
            >
              前へ
            </button>
            <span>
              {affiliatePage + 1} / {totalPages}
            </span>
            <button
              type="button"
              style={btnPager}
              onClick={handleNext}
              disabled={
                (affiliatePage + 1) * AFF_ROWS_PER_PAGE >=
                affiliateTotalCount
              }
            >
              次へ
            </button>
          </div>
        </div>
      )}

      {/* 選択中アフィの売上詳細（課金履歴） */}
      {showAffiliateDetail && (affiliateSalesLoading || salesRows.length > 0) && (
        <div>
          <h3
            style={{
              marginTop: 16,
              marginBottom: 8,
              fontSize: 16,
              fontWeight: 800,
            }}
          >
            売上詳細
          </h3>
          <div
            style={{
              marginBottom: 8,
              fontSize: 13,
              color: "#64748b",
            }}
          >
            {salesAffiliateId && (
              <>
                グループ: <b>{salesGroupName || "-"}</b>{" "}
                ／ アフィリエイター:{" "}
                <b>{salesAffiliateName || salesAffiliateId}</b> ／ ID:{" "}
                <b>{salesAffiliateId}</b>
                <br />
                紐づきユーザー数:{" "}
                <b>{salesUserCount.toLocaleString()} 人</b> ／ 課金回数:{" "}
                <b>{salesOrderCount.toLocaleString()} 件</b> ／ 売上合計:{" "}
                <b>¥{salesTotal.toLocaleString()}</b>
              </>
            )}
          </div>

          {/* 明細テーブル */}
          <div
            style={{
              overflowX: "auto",
              background: "#fff",
              border: "1px solid #e5e7eb",
              borderRadius: 12,
            }}
          >
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: "#f8fafc" }}>
                  <th style={th}>注文ID</th>
                  <th style={th}>ユーザー名</th>
                  <th style={th}>ユーザーID</th>
                  <th style={th}>プラン名</th>
                  <th style={{ ...th, textAlign: "right" }}>金額(円)</th>
                  <th style={th}>購入日時</th>
                </tr>
              </thead>
              <tbody>
                {affiliateSalesLoading ? (
                  <tr>
                    <td
                      colSpan={6}
                      style={{
                        padding: 16,
                        textAlign: "center",
                        color: "#94a3b8",
                      }}
                    >
                      読み込み中…
                    </td>
                  </tr>
                ) : (
                  salesRows.map((r, i) => {
                    const amount = safeNumber(r.amountJPY, 0);
                    const dt = r.purchasedAt;
                    return (
                      <tr
                        key={`${r.id ?? i}`}
                        style={{ borderTop: "1px solid #eef2f7" }}
                      >
                        <td style={td}>{r.id ?? "-"}</td>
                        <td style={td}>{r.userName ?? "-"}</td>
                        <td style={td}>{r.userId ?? "-"}</td>
                        <td style={td}>{r.planName ?? `#${r.planId ?? "-"}`}</td>
                        <td style={{ ...td, textAlign: "right" }}>
                          ¥{amount.toLocaleString()}
                        </td>
                        <td style={td}>
                          {dt ? new Date(dt).toLocaleString() : "-"}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}


    </div>
  );
}

/* ========= サブビュー：グループタブ ========= */

type GroupTabProps = {
  overallTotal: number;
  overallUserCount: number;
  groupPageRows: GroupSummaryRow[];
  groupTotalCount: number;
  groupPage: number;
  onChangeGroupPage: (page: number) => void;
  onClickShowGroupDetail: (row: GroupSummaryRow) => void;
  selectedGroup: string;
  showGroupDetail: boolean; // ★ 追加
  groupDetail: GroupDetailResp | null;
  groupDetailLoading: boolean;
};


function GroupTabView(props: GroupTabProps) {
  const {
    overallTotal,
    overallUserCount,
    groupPageRows,
    groupTotalCount,
    groupPage,
    onChangeGroupPage,
    onClickShowGroupDetail,
    selectedGroup,
    showGroupDetail,          // ★ ここを追加
    groupDetail,
    groupDetailLoading,
  } = props;


  const GROUP_ROWS_PER_PAGE = 50;
  const totalPages = Math.max(
    1,
    Math.ceil(groupTotalCount / GROUP_ROWS_PER_PAGE)
  );

  const handlePrev = () => {
    onChangeGroupPage(Math.max(0, groupPage - 1));
  };
  const handleNext = () => {
    if ((groupPage + 1) * GROUP_ROWS_PER_PAGE < groupTotalCount) {
      onChangeGroupPage(groupPage + 1);
    }
  };

  const detailRows = groupDetail?.rows ?? [];
  const detailGroupName = groupDetail?.groupName ?? selectedGroup;
  const detailTotal = safeNumber(groupDetail?.totalJPY, 0);
  const detailUserCount = safeNumber(groupDetail?.userCount, 0);
  const detailAffiliateCount = safeNumber(groupDetail?.affiliateCount, 0);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {/* サイト全体サマリー */}
      <div style={{ display: "flex", gap: 16 }}>
        <SummaryCard
          label="サイト全体 売上合計"
          value={`¥${safeNumber(overallTotal, 0).toLocaleString()}`}
        />
        <SummaryCard
          label="サイト全体 紐づきユーザー数"
          value={`${safeNumber(overallUserCount, 0).toLocaleString()} 人`}
        />
      </div>

      {/* グループ一覧 */}
      <div
        style={{
          overflowX: "auto",
          background: "#fff",
          border: "1px solid #e5e7eb",
          borderRadius: 12,
        }}
      >
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ background: "#f8fafc" }}>
              <th style={th}>グループ（認証鍵）</th>
              <th style={{ ...th, textAlign: "right" }}>媒体者数</th>
              <th style={{ ...th, textAlign: "right" }}>ユーザー数</th>
              <th style={{ ...th, textAlign: "right" }}>売上額(円)</th>
              <th style={{ ...th, textAlign: "center" }}></th>
            </tr>
          </thead>
          <tbody>
            {groupPageRows.map((r) => (
              <tr
                key={r.groupName}
                style={{
                  borderTop: "1px solid #eef2f7",
                  background:
                    selectedGroup === r.groupName ? "#eff6ff" : "#fff",
                }}
              >
                <td style={td}>{r.groupName}</td>
                <td style={{ ...td, textAlign: "right" }}>
                  {(r.affiliateCount ?? 0).toLocaleString()} 名
                </td>
                <td style={{ ...td, textAlign: "right" }}>
                  {(r.userCount ?? 0).toLocaleString()} 人
                </td>
                <td style={{ ...td, textAlign: "right" }}>
                  ¥{(r.totalJPY ?? 0).toLocaleString()}
                </td>
                <td style={{ ...td, textAlign: "center" }}>
                  <button
                    type="button"
                    style={btnLink}
                    onClick={() => onClickShowGroupDetail(r)}
                  >
                    売上詳細
                  </button>
                </td>
              </tr>
            ))}
            {!groupPageRows.length && (
              <tr>
                <td
                  colSpan={5}
                  style={{
                    padding: 16,
                    textAlign: "center",
                    color: "#94a3b8",
                  }}
                >
                  データがありません
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* ページャー */}
      {groupTotalCount > GROUP_ROWS_PER_PAGE && (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            fontSize: 12,
            color: "#64748b",
          }}
        >
          <div>
            {groupTotalCount
              ? `全 ${groupTotalCount.toLocaleString()} 件中 ${
                  groupPage * GROUP_ROWS_PER_PAGE + 1
                }〜${Math.min(
                  (groupPage + 1) * GROUP_ROWS_PER_PAGE,
                  groupTotalCount
                ).toLocaleString()} 件を表示`
              : "0 件"}
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button
              type="button"
              style={btnPager}
              onClick={handlePrev}
              disabled={groupPage === 0}
            >
              前へ
            </button>
            <span>
              {groupPage + 1} / {totalPages}
            </span>
            <button
              type="button"
              style={btnPager}
              onClick={handleNext}
              disabled={
                (groupPage + 1) * GROUP_ROWS_PER_PAGE >= groupTotalCount
              }
            >
              次へ
            </button>
          </div>
        </div>
      )}

      {/* 選択中グループの詳細（グループを押したら媒体者一覧＋課金合計） */}
      {showGroupDetail && selectedGroup && (groupDetailLoading || detailRows.length > 0) && (
        <div>
          <h3
            style={{
              marginTop: 16,
              marginBottom: 8,
              fontSize: 16,
              fontWeight: 800,
            }}
          >
            グループ売上詳細
          </h3>
          <div
            style={{
              marginBottom: 8,
              fontSize: 13,
              color: "#64748b",
            }}
          >
            {detailGroupName && (
              <>
                グループ: <b>{detailGroupName}</b> ／ 媒体者数:{" "}
                <b>{detailAffiliateCount.toLocaleString()} 名</b> ／
                ユーザー数: <b>{detailUserCount.toLocaleString()} 人</b> ／
                売上合計: <b>¥{detailTotal.toLocaleString()}</b>
              </>
            )}
          </div>

          <div
            style={{
              overflowX: "auto",
              background: "#fff",
              border: "1px solid #e5e7eb",
              borderRadius: 12,
            }}
          >
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: "#f8fafc" }}>
                  <th style={th}>アフィリエイター</th>
                  <th style={th}>ID</th>
                  <th style={{ ...th, textAlign: "right" }}>ユーザー数</th>
                  <th style={{ ...th, textAlign: "right" }}>売上額(円)</th>
                </tr>
              </thead>
              <tbody>
                {groupDetailLoading ? (
                  <tr>
                    <td
                      colSpan={4}
                      style={{
                        padding: 16,
                        textAlign: "center",
                        color: "#94a3b8",
                      }}
                    >
                      読み込み中…
                    </td>
                  </tr>
                ) : (
                  detailRows.map((r) => (
                    <tr
                      key={r.affiliateId}
                      style={{ borderTop: "1px solid #eef2f7" }}
                    >
                      <td style={td}>{r.name || r.affiliateId}</td>
                      <td style={td}>{r.affiliateId}</td>
                      <td style={{ ...td, textAlign: "right" }}>
                        {(r.userCount ?? 0).toLocaleString()} 人
                      </td>
                      <td style={{ ...td, textAlign: "right" }}>
                        ¥{(r.totalJPY ?? 0).toLocaleString()}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

    </div>
  );
}

/* ========= 小物 ========= */

function SummaryCard({ label, value }: { label: string; value: string }) {
  return (
    <div
      style={{
        flex: 1,
        minWidth: 160,
        background: "#fff",
        border: "1px solid #e5e7eb",
        borderRadius: 12,
        padding: 14,
      }}
    >
      <div style={{ fontSize: 12, color: "#64748b" }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 800 }}>{value}</div>
    </div>
  );
}

const th: React.CSSProperties = {
  textAlign: "left",
  padding: "10px 12px",
  fontSize: 12,
  color: "#334155",
};

const td: React.CSSProperties = {
  padding: "10px 12px",
  fontSize: 14,
  color: "#0f172a",
};

const input: React.CSSProperties = {
  padding: "10px 12px",
  border: "1px solid #cbd5e1",
  borderRadius: 8,
  background: "#fff",
};

const btnPrimary: React.CSSProperties = {
  padding: "10px 14px",
  borderRadius: 10,
  background: "#1976d2",
  color: "#fff",
  fontWeight: 800,
  border: 0,
  cursor: "pointer",
};

const btnTab: React.CSSProperties = {
  padding: "8px 12px",
  borderRadius: 10,
  border: "1px solid #cbd5e1",
  background: "#fff",
  fontWeight: 700,
  cursor: "pointer",
};

const btnTabActive: React.CSSProperties = {
  background: "#1976d2",
  color: "#fff",
  borderColor: "#1976d2",
};

const btnPager: React.CSSProperties = {
  padding: "4px 10px",
  borderRadius: 8,
  border: "1px solid #cbd5e1",
  background: "#fff",
  cursor: "pointer",
  fontSize: 12,
};

const btnLink: React.CSSProperties = {
  padding: "6px 10px",
  borderRadius: 999,
  border: "1px solid #3b82f6",
  background: "#eff6ff",
  color: "#1d4ed8",
  cursor: "pointer",
  fontSize: 12,
  fontWeight: 700,
};
